/**
 * TagManagementDialog Component
 * 
 * Диалог управления тегами записи с popup интерфейсом
 * Включает отображение выбранных тегов, список доступных тегов,
 * и функционал сохранения изменений
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTagManagement } from '@/hooks/useTagManagement';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useUserTags, useCreateTag } from '@/hooks/useTags';
import { SelectedTagsSection } from './SelectedTagsSection';
import { AvailableTagsGrid } from './AvailableTagsGrid';
import { TagSearchInput } from './TagSearchInput';
import { EditTagDialog } from './EditTagDialog';
import { DeleteTagDialog } from './DeleteTagDialog';
import { useState, useMemo } from 'react';
import type { Tag } from '@/integrations/supabase/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTagOperations } from '@/hooks/useTagOperations';
import { useTranslation } from 'react-i18next';

interface TagManagementDialogProps {
  recordId: string;
  open: boolean;
  onClose: () => void;
  onTagsUpdated?: (updatedTags: Tag[]) => void;
  initialTags?: Tag[]; // Теги записи переданные из родительского компонента
}

export const TagManagementDialog: React.FC<TagManagementDialogProps> = ({
  recordId,
  open,
  onClose,
  onTagsUpdated,
  initialTags = []
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [editTagDialogOpen, setEditTagDialogOpen] = useState(false);
  const [deleteTagDialogOpen, setDeleteTagDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  // Загружаем теги конкретной записи с использованием initialTags
  const { data: recordTags = [] } = useQuery({
    queryKey: ['record-tags', recordId],
    queryFn: async () => {
      if (!user?.id || !recordId) return [];

      const { data, error } = await supabase
        .from('record_tags')
        .select(`
          tag:tags(*)
        `)
        .eq('record_id', recordId);

      if (error) {
        console.error('Error fetching record tags:', error);
        return [];
      }

      return data?.map(rt => rt.tag).filter(Boolean) as Tag[] || [];
    },
    enabled: !!user?.id && !!recordId && open,
    staleTime: 30 * 1000,
    initialData: initialTags, // Используем переданные теги как начальные данные
  });

  const currentRecordTags = recordTags;

  // Hook для управления состоянием тегов
  const {
    selectedTags,
    hasUnsavedChanges,
    addTag,
    removeTag,
    resetToInitial,
    saveChanges,
    isSaving,
    updateTagInSelected,
    removeTagFromSelected
  } = useTagManagement(recordId, user?.id || '', currentRecordTags);

  // Загружаем все доступные теги пользователя
  const { data: availableTags = [], isLoading: tagsLoading } = useUserTags({
    userId: user?.id || '',
    enabled: !!user?.id && open,
  });

  // Hook для создания новых тегов
  const createTagMutation = useCreateTag({
    onSuccess: (newTag) => {
      // Автоматически добавляем созданный тег к записи
      addTag(newTag);
      setSearchQuery('');
      setShowSearchDropdown(false);
    },
    onError: (error) => {
      console.error('Error creating tag:', error);
    }
  });

  // Hook для операций с тегами
  const tagOperations = useTagOperations();
  const { updateTag, deleteTag, isUpdating, isDeleting } = tagOperations;

  // Фильтруем теги для поиска
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || !availableTags.length) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    return availableTags.filter(tag =>
      tag.name.toLowerCase().includes(query)
    ).slice(0, 10); // Ограничиваем количество результатов
  }, [availableTags, searchQuery]);

  // Обработчики
  const handleClose = () => {
    if (hasUnsavedChanges) {
      const shouldClose = confirm(
        t('records.dialogs.tag_management.confirm_close')
      );
      if (!shouldClose) return;

      // Сбрасываем изменения при закрытии
      resetToInitial();
    }
    onClose();
  };

  const queryClient = useQueryClient();

  const handleSave = async () => {
    try {
      await saveChanges();

      // Инвалидируем кэши для обновления UI
      queryClient.invalidateQueries({ queryKey: ['record-tags', recordId] });
      queryClient.invalidateQueries({ queryKey: ['records'] });

      // Уведомляем родительский компонент об обновлении тегов
      if (onTagsUpdated) {
        onTagsUpdated(selectedTags);
      }

      onClose();
    } catch (error) {
      console.error('Error saving tags:', error);
    }
  };

  const handleCancel = () => {
    resetToInitial();
    onClose();
  };

  const handleCreateTag = async (tagName: string) => {
    if (!user?.id || !tagName.trim()) return;

    // Генерируем случайный цвет для нового тега
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd', '#98d8c8', '#f7dc6f'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    createTagMutation.mutate({
      userId: user.id,
      data: {
        name: tagName.trim(),
        color: randomColor
      }
    });
  };

  const handleSelectTagFromSearch = (tag: Tag) => {
    addTag(tag);
    setSearchQuery('');
    setShowSearchDropdown(false);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setShowSearchDropdown(query.trim().length > 0);
  };

  const handleEditTag = (tag: Tag) => {
    setSelectedTag(tag);
    setEditTagDialogOpen(true);
  };

  const handleRemoveTag = (tag: Tag) => {
    setSelectedTag(tag);
    setDeleteTagDialogOpen(true);
  };

  const handleSaveEditedTag = (tagId: string, data: { name: string; color: string }) => {
    updateTag({ tagId, data }, {
      onSuccess: (updatedTag) => {
        // Обновляем состояние selectedTags, если тег выбран
        updateTagInSelected(updatedTag);
        setEditTagDialogOpen(false);
        setSelectedTag(null);
      }
    });
  };

  const handleConfirmDeleteTag = (tagId: string) => {
    deleteTag(tagId, {
      onSuccess: () => {
        // Удаляем тег из selectedTags, если он был выбран
        removeTagFromSelected(tagId);
        setDeleteTagDialogOpen(false);
        setSelectedTag(null);
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl h-[100vh] sm:h-[80vh] flex flex-col p-0 gap-0 sm:rounded-lg rounded-none">
        {/* Фиксированная шапка */}
        <div className="px-6 pt-6 pb-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center justify-between text-lg font-semibold">
              <span>{t('records.dialogs.tag_management.title')}</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t('records.dialogs.tag_management.description')}
            </DialogDescription>
          </DialogHeader>

          {/* Поле поиска в шапке */}
          <div className="mt-4">
            <TagSearchInput
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              placeholder={t('records.dialogs.tag_management.search_placeholder')}
              className="w-full"
              searchResults={searchResults}
              onSelectTag={handleSelectTagFromSearch}
              onCreateTag={handleCreateTag}
              showDropdown={showSearchDropdown}
              isSearchLoading={createTagMutation.isPending}
            />
          </div>
        </div>

        {/* Прокручиваемая контентная область */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Секция выбранных тегов */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                {t('records.dialogs.tag_management.selected_tags')}
              </h3>
              <SelectedTagsSection
                selectedTags={selectedTags}
                onRemoveTag={removeTag}
              />
            </div>

            {/* Секция доступных тегов */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                {t('records.dialogs.tag_management.available_tags')}
              </h3>
              <AvailableTagsGrid
                availableTags={availableTags}
                selectedTags={selectedTags}
                searchQuery={searchQuery}
                onToggleTag={(tag) => {
                  const isSelected = selectedTags.some(t => t.id === tag.id);
                  if (isSelected) {
                    removeTag(tag.id);
                  } else {
                    addTag(tag);
                  }
                }}
                onEditTag={handleEditTag}
                onRemoveTag={handleRemoveTag}
                isLoading={tagsLoading}
              />
            </div>
          </div>
        </div>

        {/* Фиксированный подвал */}
        <div className="px-6 py-4 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {hasUnsavedChanges && (
                <span className="text-amber-600 flex items-center gap-1">
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                  {t('records.dialogs.tag_management.unsaved_changes')}
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
                size="sm"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasUnsavedChanges || isSaving}
                className="min-w-[100px]"
                size="sm"
              >
                {isSaving ? t('records.dialogs.tag_management.saving') : t('records.dialogs.tag_management.save')}
              </Button>
            </div>
          </div>
        </div>

        {/* Диалоги редактирования и удаления тегов */}
        <EditTagDialog
          tag={selectedTag}
          open={editTagDialogOpen}
          onClose={() => {
            setEditTagDialogOpen(false);
            setSelectedTag(null);
          }}
          onSave={handleSaveEditedTag}
          isSaving={isUpdating}
        />

        <DeleteTagDialog
          tag={selectedTag}
          open={deleteTagDialogOpen}
          onClose={() => {
            setDeleteTagDialogOpen(false);
            setSelectedTag(null);
          }}
          onConfirm={handleConfirmDeleteTag}
          isDeleting={isDeleting}
        />
      </DialogContent>
    </Dialog>
  );
};